import { useState, useEffect, Fragment } from 'react';
import { hoyISO, primerDiaMesISO } from '../utils/fechaHoy';

const API = '/api';
const BANCOS = ['Afirme', 'Banamex', 'Banorte', 'Banregio', 'HSBC', 'Santander', 'Scotiabank'];

function fmtMonto(valor) {
  return `$${(valor || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const label = { display: 'block', marginBottom: 4, fontSize: 13, color: '#4b5563' };
const input = { padding: 8, border: '1px solid #d1d5db', borderRadius: 4, minWidth: 140 };
const cajaSeleccion = { border: '1px solid #d1d5db', borderRadius: 4, padding: 8, maxHeight: 140, overflowY: 'auto', minWidth: 200, background: 'white' };
const opcionCheckbox = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '3px 0', cursor: 'pointer' };

function SelectorMultiple({ titulo, opciones, seleccionados, onChange }) {
  const todos = seleccionados.length === 0;
  const toggleTodos = () => onChange([]);
  const toggleUno = (valor) => {
    if (seleccionados.includes(valor)) {
      onChange(seleccionados.filter(v => v !== valor));
    } else {
      onChange([...seleccionados, valor]);
    }
  };
  return (
    <div>
      <label style={label}>{titulo}</label>
      <div style={cajaSeleccion}>
        <label style={{ ...opcionCheckbox, fontWeight: 'bold', borderBottom: '1px solid #e5e7eb', marginBottom: 4, paddingBottom: 6 }}>
          <input type="checkbox" checked={todos} onChange={toggleTodos} />
          Todos
        </label>
        {opciones.map(o => (
          <label key={o.value} style={opcionCheckbox}>
            <input type="checkbox" checked={!todos && seleccionados.includes(o.value)} onChange={() => toggleUno(o.value)} />
            {o.label}
          </label>
        ))}
      </div>
    </div>
  );
}

export default function Estadisticas() {
  const [fechaInicio, setFechaInicio] = useState(primerDiaMesISO());
  const [fechaFin, setFechaFin] = useState(hoyISO());
  const [brokers, setBrokers] = useState([]);
  const [brokersSel, setBrokersSel] = useState([]);
  const [bancosSel, setBancosSel] = useState([]);
  const [reporte, setReporte] = useState('broker');
  const [filas, setFilas] = useState([]);
  const [buscado, setBuscado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API}/brokers`)
      .then(r => r.json())
      .then(data => setBrokers(Array.isArray(data) ? data : []))
      .catch(() => setBrokers([]));
  }, []);

  const buscar = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        broker_ids: brokersSel.length ? brokersSel.join(',') : 'todos',
        bancos: bancosSel.length ? bancosSel.join(',') : 'todos',
      });
      const r = await fetch(`${API}/estadisticas/prospectos-banco?${params.toString()}`);
      const data = await r.json();
      if (!r.ok) {
        setError(data.error || 'Error al cargar el reporte');
        setFilas([]);
        return;
      }
      setFilas(data);
      setBuscado(true);
    } catch {
      setError('Error de conexión con el servidor');
      setFilas([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Reporte 1: por Broker, corte y total por broker ──
  const gruposBroker = [];
  {
    const mapa = new Map();
    for (const f of filas) {
      if (!mapa.has(f.broker_id)) mapa.set(f.broker_id, { nombre: f.broker_nombre, filas: [], total: 0 });
      const g = mapa.get(f.broker_id);
      g.filas.push(f);
      g.total += f.monto_credito;
    }
    for (const g of mapa.values()) gruposBroker.push(g);
    gruposBroker.sort((a, b) => a.nombre.localeCompare(b.nombre));
  }

  // ── Reporte 2: por Banco, corte y total por banco ──
  const gruposBanco = [];
  {
    const mapa = new Map();
    for (const f of filas) {
      if (!mapa.has(f.banco)) mapa.set(f.banco, { nombre: f.banco, filas: [], total: 0 });
      const g = mapa.get(f.banco);
      g.filas.push(f);
      g.total += f.monto_credito;
    }
    for (const g of mapa.values()) gruposBanco.push(g);
    gruposBanco.sort((a, b) => a.nombre.localeCompare(b.nombre));
  }

  const totalGeneral = filas.reduce((acc, f) => acc + f.monto_credito, 0);

  const btnTab = (activo) => ({
    padding: '9px 18px',
    border: '1px solid #1a3a6e',
    borderRadius: 4,
    backgroundColor: activo ? '#1a3a6e' : 'white',
    color: activo ? 'white' : '#1a3a6e',
    fontWeight: 'bold',
    cursor: 'pointer',
  });

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ color: '#1a3a6e', marginBottom: 16 }}>Estadísticas</h2>

      <div style={{ display: 'flex', gap: 16, marginBottom: 20, background: 'white', padding: 16, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div>
          <label style={label}>Fecha inicio</label>
          <input type="date" style={input} value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} />
        </div>
        <div>
          <label style={label}>Fecha fin</label>
          <input type="date" style={input} value={fechaFin} onChange={e => setFechaFin(e.target.value)} />
        </div>
        <SelectorMultiple
          titulo="Broker"
          opciones={brokers.map(b => ({ value: String(b.id), label: b.nombre }))}
          seleccionados={brokersSel}
          onChange={setBrokersSel}
        />
        <SelectorMultiple
          titulo="Banco"
          opciones={BANCOS.map(b => ({ value: b, label: b }))}
          seleccionados={bancosSel}
          onChange={setBancosSel}
        />
        <div style={{ alignSelf: 'flex-end' }}>
          <button
            onClick={buscar}
            disabled={loading}
            style={{ padding: '9px 20px', border: 'none', borderRadius: 4, backgroundColor: '#1a3a6e', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
          >
            {loading ? 'Generando...' : 'Generar'}
          </button>
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', marginBottom: 12 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <button style={btnTab(reporte === 'broker')} onClick={() => setReporte('broker')}>
          Prospectos por Broker y Banco a Ingresar
        </button>
        <button style={btnTab(reporte === 'banco')} onClick={() => setReporte('banco')}>
          Prospectos Armados por Banco
        </button>
      </div>

      {!loading && !buscado && (
        <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: 24, textAlign: 'center', color: '#6b7280' }}>
          Seleccione un rango de fechas y presione "Generar"
        </div>
      )}

      {loading && (
        <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: 24, textAlign: 'center', color: '#6b7280' }}>
          Generando...
        </div>
      )}

      {!loading && buscado && filas.length === 0 && (
        <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: 24, textAlign: 'center', color: '#6b7280' }}>
          Sin resultados para los filtros seleccionados
        </div>
      )}

      {!loading && buscado && filas.length > 0 && reporte === 'broker' && (
        <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                <th style={{ padding: 10 }}>Broker Hipotecario</th>
                <th style={{ padding: 10 }}>Prospecto (Nombre)</th>
                <th style={{ padding: 10 }}>Banco a Ingresar</th>
                <th style={{ padding: 10, textAlign: 'right' }}>Monto de Crédito</th>
              </tr>
            </thead>
            <tbody>
              {gruposBroker.map(g => (
                <Fragment key={g.nombre}>
                  {g.filas.map((f, i) => (
                    <tr key={`${f.prospecto_id}-${f.banco}`} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={{ padding: 10 }}>{i === 0 ? g.nombre : ''}</td>
                      <td style={{ padding: 10 }}>{f.prospecto_nombre}</td>
                      <td style={{ padding: 10 }}>{f.banco}</td>
                      <td style={{ padding: 10, textAlign: 'right' }}>{fmtMonto(f.monto_credito)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '2px solid #d1d5db', background: '#f9fafb' }}>
                    <td colSpan={3} style={{ padding: 10, fontWeight: 'bold', color: '#1a3a6e' }}>
                      Total {g.nombre}
                    </td>
                    <td style={{ padding: 10, textAlign: 'right', fontWeight: 'bold', color: '#1a3a6e' }}>
                      {fmtMonto(g.total)}
                    </td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && buscado && filas.length > 0 && reporte === 'banco' && (
        <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                <th style={{ padding: 10 }}>Banco Seleccionado</th>
                <th style={{ padding: 10 }}>Prospecto (Nombre)</th>
                <th style={{ padding: 10, textAlign: 'right' }}>Monto de Crédito</th>
              </tr>
            </thead>
            <tbody>
              {gruposBanco.map(g => (
                <Fragment key={g.nombre}>
                  {g.filas.map((f, i) => (
                    <tr key={`${f.prospecto_id}-${f.banco}`} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={{ padding: 10 }}>{i === 0 ? g.nombre : ''}</td>
                      <td style={{ padding: 10 }}>{f.prospecto_nombre}</td>
                      <td style={{ padding: 10, textAlign: 'right' }}>{fmtMonto(f.monto_credito)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '2px solid #d1d5db', background: '#f9fafb' }}>
                    <td colSpan={2} style={{ padding: 10, fontWeight: 'bold', color: '#1a3a6e' }}>
                      Total {g.nombre}
                    </td>
                    <td style={{ padding: 10, textAlign: 'right', fontWeight: 'bold', color: '#1a3a6e' }}>
                      {fmtMonto(g.total)}
                    </td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && buscado && filas.length > 0 && (
        <div style={{ marginTop: 20, background: 'white', padding: 16, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ fontWeight: 'bold', color: '#1a3a6e' }}>
            Total general: {fmtMonto(totalGeneral)} ({filas.length} registro{filas.length === 1 ? '' : 's'})
          </div>
        </div>
      )}
    </div>
  );
}
